import { BUTTON_BOX_STYLES } from "asset/base-theme";
import addFolderIconHover from "asset/img/icons/add-folder-hover.svg";
import addFolderIcon from "asset/img/icons/add-folder-normal.svg";
import sendMessageIcon from "asset/img/icons/send-message.svg";
import sendMessageHoverIcon from "asset/img/icons/send_message_hover.svg";
import TransparentMask from "component/TransparentMask/TransparentMask";
import BaseButton, { IBaseButtonProps } from "component/base/BaseButton";
import { IButtonCommonStyleProps } from "component/interface/Button";
import { Tooltip } from "primereact/tooltip";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { useTranslation } from "react-i18next";
import { generateClassesForButton } from "util/Util";
import { v4 as uuidv4 } from "uuid";

interface IconButtonType {
  ADD_FOLDER: "ADD_FOLDER";
  SEND_MESSAGE: "SEND_MESSAGE";
}

interface ButtonType {
  icon: string;
  disabledIcon: string;
  hoveredIcon: string;
  selectedIcon: string;
  tooltip: string;
}

export const ICON_BUTTON: IconButtonType = {
  ADD_FOLDER: "ADD_FOLDER",
  SEND_MESSAGE: "SEND_MESSAGE",
};

const BUTTON_TYPE: { [key: string]: ButtonType } = {
  ADD_FOLDER: {
    icon: addFolderIcon,
    disabledIcon: addFolderIcon,
    hoveredIcon: addFolderIcon,
    selectedIcon: addFolderIconHover,
    tooltip: "Add Folder",
  },
  SEND_MESSAGE: {
    icon: sendMessageIcon,
    disabledIcon: sendMessageIcon,
    hoveredIcon: sendMessageHoverIcon,
    selectedIcon: sendMessageIcon,
    tooltip: "Add Folder",
  },
};
type ButtonBoxVariants = keyof typeof BUTTON_BOX_STYLES;
interface IButtonBoxProps extends IBaseButtonProps, IButtonCommonStyleProps {
  buttonType?: keyof IconButtonType;
  uid?: string;
  hideTooltip?: boolean;
  selected?: boolean;
  classNames?: string;
  variantType?: ButtonBoxVariants;
  isIcon?: boolean;
  enableMaskWhileLoading?: boolean;
  comingSoon?: boolean;
  tooltipPosition?: "top" | "bottom" | "left" | "right";
  shortcutKey?: string;
  customTooltipMessage?: string;
}

const useButtonIcon = (
  buttonType: keyof IconButtonType | undefined,
  selected?: boolean,
  disabled?: boolean
) => {
  const [iconBtnImg, setIconBtnImg] = useState<string>("");

  const onHover = useCallback(() => {
    if (!disabled && buttonType) {
      setIconBtnImg(BUTTON_TYPE[buttonType].hoveredIcon);
    }
  }, [disabled, buttonType]);

  const onLeave = useCallback(() => {
    if (!buttonType) return;

    if (selected) setIconBtnImg(BUTTON_TYPE[buttonType].selectedIcon);
    else if (disabled) setIconBtnImg(BUTTON_TYPE[buttonType].disabledIcon);
    else setIconBtnImg(BUTTON_TYPE[buttonType].icon);
  }, [buttonType, selected, disabled]);

  return { iconBtnImg, onHover, onLeave };
};

const ButtonBox: React.FC<IButtonBoxProps> = ({
  enableMaskWhileLoading,
  ...props
}: IButtonBoxProps) => {
  const [showLoader, setShowLoader] = useState(false);
  const { t } = useTranslation();

  const {
    color,
    variantType,
    onClickWithLoader,
    args,
    iconImg,
    buttonType,
    selected,
    isIcon,
    comingSoon,
    tooltipPosition,
    showButtonLoader,
    shortcutKey,
    customTooltipMessage,
    ...primeReactProps
  } = props;

  const { value, name, label, iconPos, disabled, loadingIcon, loading, icon, onClick } =
    primeReactProps;

  // Generate unique ID for this button instance
  const buttonId = useMemo(() => {
    return `button-${uuidv4()}`;
  }, []);

  const getClassNames = useCallback(() => {
    const styleObject = {
      ...BUTTON_BOX_STYLES,
      overrideStyle: props.styleObj,
      overrideClasses: props.className,
      isDisabled: props.disabled,
      removeStyleProperty: [],
      removeClasses: [],
    };

    return generateClassesForButton(styleObject, props.variantType, props.color);
  }, [props.styleObj, props.className, props.disabled, props.variantType, props.color]);

  const translatedLabel = t(label ?? "", args);

  useEffect(() => {
    if (showButtonLoader) {
      setShowLoader(true);
    } else {
      setShowLoader(false);
    }
  }, [showButtonLoader]);

  const onHandlerClick = useCallback(
    async (e: any) => {
      if (onClick) {
        onClick(e);
      } else if (onClickWithLoader) {
        setShowLoader(true);
        onClickWithLoader()
          .then(() => {
            setShowLoader(false);
          })
          .catch(() => {
            setShowLoader(false);
          });
      }
    },
    [primeReactProps.onClick, onClickWithLoader]
  );

  const { iconBtnImg, onHover, onLeave } = useButtonIcon(
    buttonType,
    selected,
    primeReactProps.disabled
  );

  useEffect(() => {
    const container = document.getElementById("transparent-mask");
    if (showLoader && enableMaskWhileLoading) {
      if (container && !container.innerHTML) {
        const containerRoot = createRoot(container);
        containerRoot.render(React.createElement(TransparentMask));
      }
    } else if (container) {
      container.innerHTML = "";
    }

    return () => {
      if (container) {
        container.innerHTML = "";
      }
    };
  }, [showLoader, enableMaskWhileLoading]);

  const buttonIcon = useMemo(() => {
    if (iconBtnImg) return iconBtnImg;
    if (buttonType) return BUTTON_TYPE[buttonType].icon;
    return iconImg;
  }, [iconImg, iconBtnImg, buttonType]);

  return (
    <>
      <BaseButton
        {...primeReactProps}
        color={color}
        className={getClassNames()}
        value={value}
        name={name}
        label={!iconImg ? translatedLabel : undefined}
        loadingIcon={loadingIcon}
        iconPos={iconPos ?? "right"}
        icon={icon}
        onClick={onHandlerClick}
        loading={showLoader || loading}
        disabled={showLoader || disabled}
        onMouseEnter={onHover}
        onMouseLeave={onLeave}
        id={buttonId}
      >
        {iconImg && (
          <div className={`flex w-full items-center gap-x-1.5 ${isIcon && "justify-center"}`}>
            {isIcon ? (
              <div>{iconImg}</div>
            ) : (
              <div className="flex items-center justify-center border-r border-dark-oauth-btn-border-hover px-6">
                <img src={iconImg} alt={"icon"} className="" height={20} width={20} />
              </div>
            )}

            <div className={`flex items-center ${!isIcon && "flex-1 justify-center"}`}>
              {translatedLabel}
            </div>
          </div>
        )}
        {variantType === "icon" && buttonType && (
          <img src={buttonIcon} alt={BUTTON_TYPE[buttonType].tooltip} />
        )}
        {shortcutKey && (
          <span className="mr-2 rounded-sm bg-light-grey-300 p-0.5 px-1 text-xxs text-light-grey-600 dark:bg-dark-neutral-gray-200 dark:text-dark-neutral-gray-700">
            {shortcutKey}
          </span>
        )}
      </BaseButton>

      {(comingSoon || customTooltipMessage) && (
        <Tooltip
          className="dark-tooltip rounded-toastBorderRadius bg-dark-neutral-gray-800 p-3 text-center text-sm !text-dark-neutral-gray-100"
          target={`#${buttonId}`}
          position={tooltipPosition ?? "bottom"}
          showDelay={70}
          hideDelay={120}
        >
          {comingSoon ? "Coming soon...🚀" : customTooltipMessage}
        </Tooltip>
      )}
    </>
  );
};

// Memoization

// redux wiring

// exports
export default memo(ButtonBox);
